import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Drawer,
  EmptyState,
  Input,
  PageHeader,
  Spinner,
  useToast,
} from "../../design-system";
import {
  downloadDocument,
  getDocumentRequests,
  getDocuments,
} from "../../helpers/documents_helper";
import { unwrap } from "../Documentos/helpers";
import { DocumentsTable } from "../Documentos/components/DocumentsTable";
import { DocumentDetail } from "../Documentos/components/DocumentDetail";
import { PendingRequestsBanner } from "../Documentos/components/PendingRequestsBanner";
import { FulfillRequestDrawer } from "../Documentos/components/FulfillRequestDrawer";
import { useDocumentDetail } from "../Documentos/useDocumentDetail";

function MisDocumentos() {
  document.title = "Mis documentos | Kronaly";
  const toast = useToast();

  const [docs, setDocs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [openDoc, setOpenDoc] = useState(null);
  const [fulfillingRequest, setFulfillingRequest] = useState(null);

  const { events, eventsLoading, previewUrl, previewLoading, previewError } =
    useDocumentDetail(openDoc);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // El backend ya filtra por rol: para "employee" solo devuelve
      // publicados visibles para el actor + de visibilidad workspace,
      // y solo las solicitudes dirigidas a este empleado.
      const [docsRes, reqRes] = await Promise.all([
        getDocuments(),
        getDocumentRequests().catch(() => ({ data: [] })),
      ]);
      setDocs(unwrap(docsRes) || []);
      setRequests(unwrap(reqRes) || []);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar tus documentos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs
      .filter((d) => {
        if (!q) return true;
        const haystack = [
          d.title,
          d.original_filename,
          d.description,
          d.category?.name,
          d.category?.parent_name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      })
      .sort((a, b) => (b.published_at || b.id).localeCompare(a.published_at || a.id));
  }, [docs, search]);

  const onDownload = async (doc) => {
    try {
      await downloadDocument(doc.id, doc.original_filename || doc.title);
    } catch (e) {
      toast.error("No se pudo descargar", typeof e === "string" ? e : e?.message);
    }
  };

  return (
    <div className="kr-page">
      <PageHeader
        eyebrow="Mi cuenta"
        title="Mis documentos"
        description="Aquí encontrarás los documentos que RRHH ha publicado para ti."
      />

      {error && <Alert tone="danger">{error}</Alert>}

      <PendingRequestsBanner
        requests={requests}
        isManager={false}
        onFulfill={(req) => setFulfillingRequest(req)}
      />

      <Card padded={false}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "12px 16px",
            borderBottom: "1px solid var(--kr-border-subtle)",
          }}
        >
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por título, archivo o categoría…"
            icon="Search"
            style={{ flex: 1, maxWidth: 360 }}
          />
          <div style={{ flex: 1 }} />
          <span
            style={{
              fontSize: 11,
              color: "var(--kr-fg-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              fontWeight: 600,
            }}
          >
            {filtered.length} documento{filtered.length === 1 ? "" : "s"}
          </span>
        </div>

        {loading ? (
          <div style={{ padding: 32, textAlign: "center" }}>
            <Spinner size={16} />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="FileText"
            title={search.trim() ? "Sin resultados" : "Aún no tienes documentos"}
            description={
              search.trim()
                ? "Prueba con otra búsqueda."
                : "Cuando RRHH publique documentos para ti aparecerán aquí."
            }
          />
        ) : (
          <DocumentsTable
            rows={filtered}
            employeesById={{}}
            isManager={false}
            onOpen={setOpenDoc}
            onDownload={onDownload}
          />
        )}
      </Card>

      <Drawer
        open={!!openDoc}
        onClose={() => setOpenDoc(null)}
        size="lg"
        title={openDoc?.title || "Documento"}
        footer={
          openDoc ? (
            <div style={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
              <Button variant="ghost" icon="Download" onClick={() => onDownload(openDoc)}>
                Descargar
              </Button>
            </div>
          ) : null
        }
      >
        {openDoc && (
          <DocumentDetail
            doc={openDoc}
            employee={null}
            previewUrl={previewUrl}
            previewLoading={previewLoading}
            previewError={previewError}
            events={events}
            eventsLoading={eventsLoading}
          />
        )}
      </Drawer>

      <FulfillRequestDrawer
        request={fulfillingRequest}
        onClose={() => setFulfillingRequest(null)}
        onFulfilled={() => {
          setFulfillingRequest(null);
          load();
        }}
      />
    </div>
  );
}

export default MisDocumentos;
